/**
 * Unified Schema.org Structured Data Utilities
 * 
 * This module provides Google Rich Results-compliant JSON-LD schemas.
 * All schemas follow schema.org specifications and Google's guidelines.
 * 
 * Key principles:
 * - Top-level nodes use WebPage/Article (never CreativeWork as parent)
 * - SportsEvent always includes required 'location' field
 * - Separate JSON-LD blocks for different schema types to avoid nesting conflicts
 */

import { format } from 'date-fns';

// ============================================================================
// Types
// ============================================================================

export interface LocationData {
  venue?: string | null;
  city?: string | null;
  country?: string | null;
}

export interface TeamData {
  name: string;
  logo?: string | null;
}

export interface LeagueData {
  name: string;
  logo_url?: string | null;
  country?: string | null;
}

export interface MatchData {
  id?: string;
  home_team: string;
  away_team: string;
  kickoff_time: string;
  venue?: string | null;
  venue_city?: string | null;
  league?: LeagueData | null;
  status: string;
  home_score?: number | null;
  away_score?: number | null;
  home_team_logo?: string | null;
  away_team_logo?: string | null;
}

export interface CouponLegData {
  match?: MatchData | null;
  market_type: string;
  selection: string;
  odds_at_creation: number;
}

export interface FAQItem {
  question: string;
  answer: string;
}

export interface BreadcrumbItem {
  name: string;
  url: string;
}

// ============================================================================
// Constants
// ============================================================================

// Country name to ISO 3166-1 alpha-2 mapping
const COUNTRY_CODE_MAP: Record<string, string> = {
  'England': 'GB',
  'United Kingdom': 'GB',
  'Spain': 'ES',
  'Italy': 'IT',
  'Germany': 'DE',
  'France': 'FR',
  'Netherlands': 'NL',
  'Portugal': 'PT',
  'Turkey': 'TR',
  'Belgium': 'BE',
  'Scotland': 'GB',
  'Wales': 'GB',
  'Mexico': 'MX',
  'USA': 'US',
  'United States': 'US',
  'Brazil': 'BR',
  'Argentina': 'AR',
  'World': 'INT',
  'Europe': 'EU',
  'International': 'INT',
  'Greece': 'GR',
  'Austria': 'AT',
  'Switzerland': 'CH',
  'Poland': 'PL',
  'Ukraine': 'UA',
  'Russia': 'RU',
  'Denmark': 'DK',
  'Sweden': 'SE',
  'Norway': 'NO',
  'Finland': 'FI',
  'Czech Republic': 'CZ',
  'Croatia': 'HR',
  'Serbia': 'RS',
  'Romania': 'RO',
  'Hungary': 'HU',
  'Bulgaria': 'BG',
  'Japan': 'JP',
  'South Korea': 'KR',
  'China': 'CN',
  'Australia': 'AU',
  'Saudi Arabia': 'SA',
  'Qatar': 'QA',
  'UAE': 'AE',
  'Egypt': 'EG',
  'Morocco': 'MA',
  'Nigeria': 'NG',
  'South Africa': 'ZA',
};

const ORGANIZATION_SCHEMA = {
  '@type': 'Organization' as const,
  '@id': 'https://winwgo.com/#organization',
  name: 'WinWGo',
  url: 'https://winwgo.com',
  logo: {
    '@type': 'ImageObject' as const,
    url: 'https://winwgo.com/branding/logo-desktop.png',
    width: 512,
    height: 512,
  },
};

const WEBSITE_SCHEMA = {
  '@type': 'WebSite' as const,
  '@id': 'https://winwgo.com/#website',
  name: 'WinWGo',
  url: 'https://winwgo.com',
  publisher: { '@id': 'https://winwgo.com/#organization' },
};

// ============================================================================
// Location Schema Helpers
// ============================================================================

/**
 * Get ISO country code from country name
 */
export function getCountryCode(country?: string | null): string {
  if (!country) return 'EU';
  return COUNTRY_CODE_MAP[country] || country.slice(0, 2).toUpperCase();
}

/**
 * Generate a valid Place schema with PostalAddress
 * REQUIRED for SportsEvent to pass Google Rich Results validation
 */
export function generateLocationSchema(data: LocationData): {
  '@type': 'Place';
  name: string;
  address: {
    '@type': 'PostalAddress';
    addressLocality: string;
    addressCountry: string;
  };
} {
  const venueName = data.venue || 'Stadium';
  const country = data.country || 'Europe';
  // Use city if provided, otherwise extract from venue or use country
  const city = data.city || (data.venue ? data.venue.split(',')[0].trim() : country);
  
  return {
    '@type': 'Place',
    name: venueName,
    address: {
      '@type': 'PostalAddress',
      addressLocality: city,
      addressCountry: getCountryCode(country),
    },
  };
}

// ============================================================================
// Event Status Mapping
// ============================================================================

/**
 * Map match status to schema.org EventStatus
 */
export function getEventStatus(status: string): string {
  switch (status) {
    case 'live':
    case 'scheduled':
    case 'finished':
      return 'https://schema.org/EventScheduled';
    case 'postponed':
      return 'https://schema.org/EventPostponed';
    case 'cancelled':
      return 'https://schema.org/EventCancelled';
    default:
      return 'https://schema.org/EventScheduled';
  }
}

// ============================================================================
// SportsEvent Schema
// ============================================================================

/**
 * Generate a complete, valid SportsEvent schema
 * Includes all required fields (especially location) and recommended fields
 */
export function generateSportsEventSchema(
  match: MatchData,
  canonicalUrl: string
): Record<string, unknown> {
  const kickoffDate = new Date(match.kickoff_time);
  const endDate = new Date(kickoffDate.getTime() + 2 * 60 * 60 * 1000); // +2 hours
  const eventUrl = canonicalUrl;
  
  return {
    '@context': 'https://schema.org',
    '@type': 'SportsEvent',
    '@id': `${eventUrl}#event`,
    name: `${match.home_team} vs ${match.away_team}`,
    description: `${match.league?.name || 'Football'} match: ${match.home_team} vs ${match.away_team}. Expert prediction and betting tips available.`,
    startDate: kickoffDate.toISOString(),
    endDate: endDate.toISOString(),
    eventStatus: getEventStatus(match.status),
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    
    // REQUIRED: location field
    location: generateLocationSchema({
      venue: match.venue,
      city: match.venue_city,
      country: match.league?.country,
    }),
    
    // Sport specification
    sport: 'https://en.wikipedia.org/wiki/Association_football',
    
    // Teams
    homeTeam: {
      '@type': 'SportsTeam',
      name: match.home_team,
      ...(match.home_team_logo && { logo: match.home_team_logo }),
    },
    awayTeam: {
      '@type': 'SportsTeam',
      name: match.away_team,
      ...(match.away_team_logo && { logo: match.away_team_logo }),
    },
    competitor: [
      {
        '@type': 'SportsTeam',
        name: match.home_team,
        ...(match.home_team_logo && { logo: match.home_team_logo }),
      },
      {
        '@type': 'SportsTeam',
        name: match.away_team,
        ...(match.away_team_logo && { logo: match.away_team_logo }),
      },
    ],
    
    // Organizer (league)
    ...(match.league && {
      organizer: {
        '@type': 'SportsOrganization',
        name: match.league.name,
        ...(match.league.logo_url && { logo: match.league.logo_url }),
      },
    }),
    
    // Image
    ...(match.home_team_logo && { image: match.home_team_logo }),
    
    // Offer for prediction
    offers: {
      '@type': 'Offer',
      name: 'Expert Match Prediction',
      description: 'Access expert prediction and betting tips',
      price: '1',
      priceCurrency: 'USD',
      url: eventUrl,
      validFrom: new Date(kickoffDate.getTime() - 24 * 60 * 60 * 1000).toISOString(),
      availability: 'https://schema.org/InStock',
      seller: ORGANIZATION_SCHEMA,
    },
    
    url: eventUrl,
    inLanguage: 'en-US',
  };
}

/**
 * Generate SportsEvent schema for a coupon leg
 */
export function generateCouponLegSportsEventSchema(
  leg: CouponLegData,
  baseUrl: string
): Record<string, unknown> | null {
  const match = leg.match;
  if (!match) return null;
  
  const kickoffDate = new Date(match.kickoff_time);
  const endDate = new Date(kickoffDate.getTime() + 2 * 60 * 60 * 1000);
  const eventUrl = `https://winwgo.com/match/${match.id || 'match'}`;
  
  return {
    '@context': 'https://schema.org',
    '@type': 'SportsEvent',
    '@id': `${eventUrl}#event`,
    name: `${match.home_team} vs ${match.away_team}`,
    description: `${match.league?.name || 'Football'} match: ${match.home_team} vs ${match.away_team}. Tip: ${leg.market_type} - ${leg.selection}`,
    startDate: kickoffDate.toISOString(),
    endDate: endDate.toISOString(),
    eventStatus: getEventStatus(match.status || 'scheduled'),
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    
    // REQUIRED: location field
    location: generateLocationSchema({
      venue: match.venue,
      city: match.venue_city,
      country: match.league?.country,
    }),
    
    sport: 'https://en.wikipedia.org/wiki/Association_football',
    
    competitor: [
      {
        '@type': 'SportsTeam',
        name: match.home_team,
        ...(match.home_team_logo && { logo: match.home_team_logo }),
      },
      {
        '@type': 'SportsTeam',
        name: match.away_team,
        ...(match.away_team_logo && { logo: match.away_team_logo }),
      },
    ],
    
    ...(match.league && {
      organizer: {
        '@type': 'SportsOrganization',
        name: match.league.name,
      },
    }),
    
    ...(match.home_team_logo && { image: match.home_team_logo }),
    
    offers: {
      '@type': 'Offer',
      name: `${leg.market_type}: ${leg.selection}`,
      description: `Betting tip: ${leg.market_type} - ${leg.selection} @ ${leg.odds_at_creation}`,
      price: '1',
      priceCurrency: 'USD',
      url: eventUrl,
      validFrom: new Date(kickoffDate.getTime() - 24 * 60 * 60 * 1000).toISOString(),
      availability: 'https://schema.org/InStock',
    },
    
    url: eventUrl,
  };
}

// ============================================================================
// WebPage Schema
// ============================================================================

/**
 * Generate a valid WebPage schema (top-level node)
 */
export function generateWebPageSchema(options: {
  url: string;
  name: string;
  description: string;
  datePublished?: string;
  dateModified?: string;
  primaryImage?: string;
  breadcrumb?: BreadcrumbItem[];
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': options.url,
    url: options.url,
    name: options.name,
    description: options.description,
    inLanguage: 'en-US',
    isPartOf: WEBSITE_SCHEMA,
    ...(options.datePublished && { datePublished: options.datePublished }),
    ...(options.dateModified && { dateModified: options.dateModified }),
    ...(options.primaryImage && {
      primaryImageOfPage: {
        '@type': 'ImageObject',
        url: options.primaryImage,
        width: 1200,
        height: 630,
      },
    }),
    publisher: ORGANIZATION_SCHEMA,
    author: ORGANIZATION_SCHEMA,
  };
}

// ============================================================================
// Article Schema
// ============================================================================

/**
 * Generate Article schema for prediction/analysis content
 */
export function generateArticleSchema(options: {
  url: string;
  headline: string;
  description: string;
  datePublished?: string;
  dateModified?: string;
  image?: string;
  keywords?: string[];
}): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `${options.url}#article`,
    headline: options.headline,
    description: options.description,
    url: options.url,
    datePublished: options.datePublished || new Date().toISOString(),
    dateModified: options.dateModified || new Date().toISOString(),
    author: ORGANIZATION_SCHEMA,
    publisher: ORGANIZATION_SCHEMA,
    mainEntityOfPage: { '@type': 'WebPage', '@id': options.url },
    articleSection: 'Football Predictions',
    ...(options.keywords && { keywords: options.keywords.join(', ') }),
    ...(options.image && {
      image: {
        '@type': 'ImageObject',
        url: options.image,
      },
    }),
    inLanguage: 'en-US',
  };
}

// ============================================================================
// FAQ Schema
// ============================================================================

/**
 * Generate FAQPage schema
 */
export function generateFAQPageSchema(faqs: FAQItem[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

// ============================================================================
// Breadcrumb Schema
// ============================================================================

/**
 * Generate BreadcrumbList schema
 */
export function generateBreadcrumbListSchema(items: BreadcrumbItem[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

// ============================================================================
// ItemList Schema (for coupons)
// ============================================================================

/**
 * Generate ItemList schema for coupon listings
 */
export function generateCouponListSchema(options: {
  name: string;
  description: string;
  items: Array<{
    name: string;
    description?: string;
    url: string;
  }>;
}): Record<string, unknown> {
  return {
    '@type': 'ItemList',
    name: options.name,
    description: options.description,
    numberOfItems: options.items.length,
    itemListElement: options.items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      description: item.description,
      url: item.url,
    })),
  };
}

// ============================================================================
// Bookmaker Offer Schema
// ============================================================================

export interface BookmakerOfferData {
  name: string;
  description?: string | null;
  bonusText: string;
  bonusAmount?: string | null;
  affiliateLink: string;
  logoUrl?: string | null;
  currency?: string;
}

/**
 * Generate valid Offer schema for bookmaker promotions
 * Follows Google Rich Results guidelines for offers
 */
export function generateBookmakerOfferSchema(
  offer: BookmakerOfferData
): Record<string, unknown> {
  // Parse price from bonusAmount (e.g., "€200" or "$500")
  const priceMatch = offer.bonusAmount?.match(/[\d,.]+/);
  const price = priceMatch ? priceMatch[0].replace(',', '') : '0';
  
  // Detect currency from bonus text
  const currencyMap: Record<string, string> = {
    '€': 'EUR',
    '$': 'USD', 
    '£': 'GBP',
    'R$': 'BRL',
  };
  const currencySymbol = offer.bonusAmount?.match(/[€$£]|R\$/)?.[0];
  const currency = offer.currency || (currencySymbol ? currencyMap[currencySymbol] : 'EUR');

  return {
    '@context': 'https://schema.org',
    '@type': 'Offer',
    name: `${offer.name} - ${offer.bonusText}`,
    description: offer.description || `Claim your ${offer.bonusText} bonus at ${offer.name}. New customers only, T&Cs apply.`,
    url: offer.affiliateLink,
    price,
    priceCurrency: currency,
    availability: 'https://schema.org/InStock',
    validFrom: new Date().toISOString(),
    seller: {
      '@type': 'Organization',
      name: offer.name,
      ...(offer.logoUrl && { logo: offer.logoUrl }),
    },
    category: 'Sports Betting Bonus',
    itemCondition: 'https://schema.org/NewCondition',
  };
}

/**
 * Generate ItemList schema for multiple bookmaker offers
 */
export function generateBookmakerOffersListSchema(
  offers: BookmakerOfferData[],
  pageUrl: string
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Betting Offers & Bonuses',
    description: 'Exclusive betting offers and welcome bonuses from verified bookmakers',
    url: pageUrl,
    numberOfItems: offers.length,
    itemListElement: offers.map((offer, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Offer',
        name: `${offer.name} - ${offer.bonusText}`,
        description: offer.description || `${offer.bonusText} at ${offer.name}`,
        url: offer.affiliateLink,
      },
    })),
  };
}

// ============================================================================
// Combined Schema for Match Pages
// ============================================================================

/**
 * Generate complete structured data for a match detail page
 * Returns separate JSON-LD blocks to avoid parent-node conflicts
 */
export function generateMatchPageSchemas(
  match: MatchData,
  slug: string
): {
  webPage: Record<string, unknown>;
  sportsEvent: Record<string, unknown>;
  article: Record<string, unknown>;
  breadcrumb: Record<string, unknown>;
} {
  const matchUrl = `https://winwgo.com/match/${slug}`;
  const displayDate = format(new Date(match.kickoff_time), 'MMM d, yyyy');
  
  const headline = `${match.home_team} vs ${match.away_team} Prediction & Betting Tips`;
  const description = `Expert ${match.home_team} vs ${match.away_team} prediction for ${displayDate}. Get AI-powered betting tips and match analysis for ${match.league?.name || 'Football'}.`;
  
  return {
    webPage: generateWebPageSchema({
      url: matchUrl,
      name: headline,
      description,
      dateModified: new Date().toISOString(),
      primaryImage: match.home_team_logo || 'https://winwgo.com/branding/og-logo.png',
    }),
    
    sportsEvent: generateSportsEventSchema(match, matchUrl),
    
    article: generateArticleSchema({
      url: matchUrl,
      headline,
      description,
      keywords: [
        match.home_team,
        match.away_team,
        match.league?.name || 'Football',
        'predictions',
        'betting tips',
      ],
    }),
    
    breadcrumb: generateBreadcrumbListSchema([
      { name: 'Home', url: 'https://winwgo.com' },
      { name: match.league?.name || 'Football', url: 'https://winwgo.com' },
      { name: `${match.home_team} vs ${match.away_team}`, url: matchUrl },
    ]),
  };
}

// ============================================================================
// Combined Schema for Coupon Pages  
// ============================================================================

/**
 * Generate complete structured data for a coupons page
 * Returns separate JSON-LD blocks to inject individually
 */
export function generateCouponsPageSchemas(options: {
  date: Date;
  canonicalUrl: string;
  title: string;
  description: string;
  dominantLeague: string;
  coupons: Array<{
    title: string;
    description?: string | null;
    legs: CouponLegData[];
  }>;
  faqs: FAQItem[];
}): {
  webPage: Record<string, unknown>;
  breadcrumb: Record<string, unknown>;
  faq: Record<string, unknown>;
  sportsEvents: Array<Record<string, unknown>>;
} {
  const dateStr = format(options.date, 'yyyy-MM-dd');
  const displayDate = format(options.date, 'd MMMM yyyy');
  
  // Generate unique SportsEvent schemas for all matches
  const sportsEventsMap = new Map<string, Record<string, unknown>>();
  
  options.coupons.forEach(coupon => {
    coupon.legs.forEach(leg => {
      if (leg.match) {
        const eventKey = `${leg.match.home_team}-${leg.match.away_team}`;
        if (!sportsEventsMap.has(eventKey)) {
          const schema = generateCouponLegSportsEventSchema(leg, options.canonicalUrl);
          if (schema) {
            sportsEventsMap.set(eventKey, schema);
          }
        }
      }
    });
  });
  
  return {
    webPage: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': options.canonicalUrl,
      url: options.canonicalUrl,
      name: options.title,
      description: options.description,
      inLanguage: 'en-US',
      datePublished: `${dateStr}T00:00:00Z`,
      dateModified: new Date().toISOString(),
      isPartOf: WEBSITE_SCHEMA,
      about: {
        '@type': 'Thing',
        name: options.dominantLeague || 'Football Betting',
        description: 'Football predictions and betting tips',
      },
      publisher: ORGANIZATION_SCHEMA,
      author: ORGANIZATION_SCHEMA,
      primaryImageOfPage: {
        '@type': 'ImageObject',
        url: 'https://winwgo.com/branding/og-logo.png',
        width: 1200,
        height: 630,
      },
      // Main content as ItemList - correctly nested
      mainEntity: {
        '@type': 'ItemList',
        name: `Daily Coupons for ${displayDate}`,
        description: `${options.coupons.length} AI-generated betting coupons`,
        numberOfItems: options.coupons.length,
        itemListElement: options.coupons.map((coupon, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: coupon.title,
          description: coupon.description || `AI-generated betting coupon with ${coupon.legs.length} selections`,
          url: `${options.canonicalUrl}#coupon-${index + 1}`,
        })),
      },
    },
    
    breadcrumb: generateBreadcrumbListSchema([
      { name: 'Home', url: 'https://winwgo.com' },
      { name: 'Daily Coupons', url: 'https://winwgo.com/coupons' },
      { name: `${options.dominantLeague} - ${displayDate}`, url: options.canonicalUrl },
    ]),
    
    faq: generateFAQPageSchema(options.faqs),
    
    sportsEvents: Array.from(sportsEventsMap.values()),
  };
}
